import React, { Suspense, lazy } from 'react';
import { Route, Navigate } from 'react-router-dom';
import { MAIN_PROCESS_STEPS, SCAN_PROCESS_STEPS, PATHS } from '../shared/constants/flowConfig';

const StepLoadingFallback = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      fontSize: '16px',
      color: '#666',
    }}
  >
    Loading step...
  </div>
);

const PreparingScanStep = lazy(() => import('../features/prepare-scan/PrepareScanStep'));
const AxesAlignmentStep = lazy(() => import('../features/alignment/AlignmentStep'));

const RectificationStep = lazy(() => import('../features/rectification/RectificationStep'));
const ElongationStep = lazy(() => import('../features/elongation/ElongationStep'));
const TrimlineStep = lazy(() => import('../features/trimline/TrimlineStep'));
// Flares v1/v2 handler selection now lives inside the migrated step
// (FlaresSceneHandlers); the step shell is the same either way.
const FlaresStep = lazy(() => import('../features/flares/FlaresStep'));
const MLReductionStep = lazy(() => import('../features/ml-reduction/MLReductionStep'));
const ThicknessStep = lazy(() => import('../features/thickness/ThicknessStep'));
const LockingMechanismStep = lazy(() => import('../features/locking-mechanism/LockingMechanismStep'));
const ValveStep = lazy(() => import('../features/valve/ValveStep'));
const LogoPlacementStep = lazy(() => import('../features/logo-placement/LogoPlacementStep'));
// const PadsStep = lazy(() => import('../features/pads/PadsStep'));
// const ValvePatternStep = lazy(() => import('../features/valve-pattern/ValvePatternStep'));
const FinalizationStep = lazy(() => import('../features/finalization/FinalizationStep'));

const wrap = (el) => <Suspense fallback={<StepLoadingFallback />}>{el}</Suspense>;

const legacyMainRedirects = [
  ['main/rectification', PATHS[MAIN_PROCESS_STEPS.RECTIFICATION]],
  ['main/elongation', PATHS[MAIN_PROCESS_STEPS.ELONGATION]],
  ['main/trimline', PATHS[MAIN_PROCESS_STEPS.TRIMLINE]],
  ['main/ml-reduction', PATHS[MAIN_PROCESS_STEPS.ML_REDUCTION]],
  ['main/flares', PATHS[MAIN_PROCESS_STEPS.FLARES]],
  ['main/thickness', PATHS[MAIN_PROCESS_STEPS.THICKNESS]],
  ['main/locking-mechanism', PATHS[MAIN_PROCESS_STEPS.LOCKING_MECHANISM]],
  ['main/alignment', PATHS[MAIN_PROCESS_STEPS.LOCKING_MECHANISM]],
  ['main/valve', PATHS[MAIN_PROCESS_STEPS.VALVE]],
  ['main/logo-placement', PATHS[MAIN_PROCESS_STEPS.LOGO_PLACEMENT]],
  // ['main/pads', PATHS[MAIN_PROCESS_STEPS.PADS]],
  ['main/valve-pattern', PATHS[MAIN_PROCESS_STEPS.VALVE_PATTERN]],
  ['main/finalization', PATHS[MAIN_PROCESS_STEPS.FINALIZATION]],
];

export const designChildRoutes = (
  <>
    <Route path="scan/preparing" element={wrap(<PreparingScanStep />)} />
    <Route path="scan/axes-alignment" element={wrap(<AxesAlignmentStep />)} />
    <Route path="scan/final-preview" element={<Navigate to={PATHS[SCAN_PROCESS_STEPS.AXES_ALIGNMENT]} replace />} />

    <Route path="clinical-modification/rectification" element={wrap(<RectificationStep />)} />
    <Route path="clinical-modification/elongation" element={wrap(<ElongationStep />)} />
    <Route path="clinical-modification/trimline" element={wrap(<TrimlineStep />)} />
    <Route path="clinical-modification/flares" element={wrap(<FlaresStep />)} />
    <Route path="clinical-modification/ml-reduction" element={wrap(<MLReductionStep />)} />
    <Route path="clinical-modification/thickness" element={wrap(<ThicknessStep />)} />
    {/* Valve moved after the locking mechanism; keep the old clinical URL working. */}
    <Route path="clinical-modification/valve" element={<Navigate to={PATHS[MAIN_PROCESS_STEPS.VALVE]} replace />} />
    <Route path="clinical-modification/preview" element={<Navigate to={PATHS[MAIN_PROCESS_STEPS.THICKNESS]} replace />} />
    {/* <Route path="clinical-modification/pads" element={wrap(<PadsStep />)} /> */}

    <Route path="alignment-and-locking/locking-mechanism" element={wrap(<LockingMechanismStep />)} />
    <Route path="alignment-and-locking/valve" element={wrap(<ValveStep />)} />
    <Route path="alignment-and-locking/logo-placement" element={wrap(<LogoPlacementStep />)} />

    <Route path="finalization" element={wrap(<FinalizationStep />)} />

    {legacyMainRedirects.map(([legacyPath, target]) => (
      <Route
        key={legacyPath}
        path={legacyPath}
        element={<Navigate to={target} replace />}
      />
    ))}

    <Route index element={<Navigate to="scan/preparing" replace />} />
  </>
);
